import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { MyRefunds } from "../services/api";
import {
  FaArrowLeft,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";
import CustomerLayout from "../components/layout/CustomerLayout";

const PAGE_SIZE = 6;

const STATUS_META = {
  pending: { label: "Pending", badge: "bg-yellow-50 text-yellow-700 border border-yellow-200", icon: FaArrowRight, iconBg: "bg-yellow-100 text-yellow-600" },
  completed: { label: "Approved", badge: "bg-green-100 text-green-700", icon: FaCheck, iconBg: "bg-green-100 text-green-600" },
  failed: { label: "Rejected", badge: "bg-red-50 text-red-600", icon: FaTimes, iconBg: "bg-red-100 text-red-500" },
};

function monthKey(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" }).toUpperCase();
}

export default function RefundHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["my-refunds"],
    queryFn: MyRefunds,
  });

  const refunds = data?.results || [];
  const walletBalance = parseFloat(user?.wallet_balance || 0);
  const REFUND_FEE = 200;
  const MIN_AMOUNT = 1000;

  const stats = useMemo(() => {
    const totalRequested = refunds.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
    const pending = refunds.filter((r) => r.status === "pending").length;
    const approved = refunds.filter((r) => r.status === "completed").length;
    const rejected = refunds.filter((r) => r.status === "failed").length;
    return { totalRequested, pending, approved, rejected };
  }, [refunds]);

  const filtered = useMemo(() => {
    if (filter === "all") return refunds;
    const map = { pending: "pending", approved: "completed", rejected: "failed" };
    return refunds.filter((r) => r.status === map[filter]);
  }, [refunds, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const grouped = useMemo(() => {
    const groups = {};
    pageItems.forEach((r) => {
      const key = monthKey(r.created_at);
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    });
    return groups;
  }, [pageItems]);

  const handleFilterChange = (f) => {
    setFilter(f);
    setPage(1);
  };

  const viewReceipt = (r) => {
    const statusForReceipt = r.status === "completed" ? "completed" : r.status === "failed" ? "failed" : "pending";
    navigate("/wallet/withdraw/receipt", {
      state: {
        status: statusForReceipt,
        withdrawalDetails: {
          amount: r.amount,
          recipient_bank_name: r.recipient_bank_name,
          recipient_account_number: r.recipient_account_number,
          recipient_account_name: r.recipient_account_name,
          transaction_description: r.transaction_description,
        },
        walletBalance,
        data: r,
      },
    });
  };

  return (
    <CustomerLayout>
      <div className="max-w-4xl mx-auto space-y-5">
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3d2e] border border-green-200 rounded-xl px-4 py-2 hover:bg-green-50 transition"
        >
          <FaArrowLeft className="w-3 h-3" /> Back to Dashboard
        </button>

        <div>
          <h1 className="text-3xl font-black text-gray-900 mb-1.5">Refund History</h1>
          <p className="text-gray-500">
            A record of all refund requests made from your UCH wallet.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-xs text-gray-400 font-medium mb-2">Total Requested</p>
            <p className="text-xl font-black text-gray-900 mb-1">
              ₦{stats.totalRequested.toLocaleString()}
            </p>
            <p className="text-xs text-gray-400">All time</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="flex items-center gap-1.5 text-xs text-gray-400 font-medium mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" /> Pending
            </p>
            <p className="text-xl font-black text-gray-900 mb-1">{stats.pending}</p>
            <p className="text-xs text-gray-400">Awaiting approval</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="flex items-center gap-1.5 text-xs text-gray-400 font-medium mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Approved
            </p>
            <p className="text-xl font-black text-gray-900 mb-1">{stats.approved}</p>
            <p className="text-xs text-gray-400">Successfully paid</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="flex items-center gap-1.5 text-xs text-gray-400 font-medium mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Rejected
            </p>
            <p className="text-xl font-black text-gray-900 mb-1">{stats.rejected}</p>
            <p className="text-xs text-gray-400">Not processed</p>
          </div>
        </div>

        {/* Balance banner */}
        <div className="bg-gradient-to-br from-[#0d3d2e] to-[#0f4a38] rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-green-300 text-xs font-semibold uppercase tracking-widest mb-1.5">
              Available Balance
            </p>
            <p className="text-3xl font-black text-white mb-1">
              ₦{walletBalance.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </p>
            <p className="text-green-200 text-xs">
              Refund Fee: ₦{REFUND_FEE} · Minimum: ₦{MIN_AMOUNT.toLocaleString()}
            </p>
          </div>
          <button
            onClick={() => navigate("/withdraw")}
            className="bg-white hover:bg-green-50 text-[#0d3d2e] font-semibold text-sm px-5 py-2.5 rounded-xl transition flex items-center gap-2"
          >
            <FaArrowRight className="w-3 h-3" /> Request Refund
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-bold text-gray-900">All Requests</h3>
          <div className="flex items-center gap-2">
            {[
              ["all", "All"],
              ["pending", "Pending"],
              ["approved", "Approved"],
              ["rejected", "Rejected"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleFilterChange(key)}
                className={`text-sm font-semibold px-4 py-1.5 rounded-lg border transition ${
                  filter === key
                    ? "border-[#0d3d2e] text-[#0d3d2e] bg-green-50"
                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="text-center py-16 text-gray-400 text-sm flex items-center justify-center gap-2">
              <FaSpinner className="animate-spin" /> Loading refund history...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              No refund requests found.
            </div>
          ) : (
            Object.entries(grouped).map(([month, items]) => (
              <div key={month}>
                <div className="px-6 py-2.5 bg-gray-50 text-xs font-bold text-gray-400 tracking-wide uppercase">
                  {month}
                </div>
                {items.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  return (
                    <div
                      key={r.reference}
                      className="px-6 py-4 flex items-center justify-between border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition flex-wrap gap-3"
                    >
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta.iconBg}`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">Wallet Refund</p>
                          <p className="text-xs text-gray-400 truncate">
                            {new Date(r.created_at).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}{" "}
                            · {r.recipient_bank_name} · {r.recipient_account_number} ·{" "}
                            {r.recipient_account_name}
                          </p>
                          {r.status === "failed" && r.failure_reason && (
                            <p className="text-xs text-red-500 mt-1">
                              Reason: {r.failure_reason}
                            </p>
                          )}
                          <button
                            onClick={() => viewReceipt(r)}
                            className="text-xs border border-gray-200 text-gray-600 px-3 py-1 rounded-lg hover:bg-gray-100 transition mt-1.5"
                          >
                            View receipt
                          </button>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-gray-900">
                          −₦{parseFloat(r.amount || 0).toLocaleString()}
                        </p>
                        <span
                          className={`inline-block mt-1 text-xs font-semibold px-2.5 py-1 rounded-full ${meta.badge}`}
                        >
                          {meta.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-500">
            <p>
              Showing <strong>{pageItems.length}</strong> of{" "}
              <strong>{filtered.length}</strong> requests
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <FaChevronLeft className="w-3 h-3" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <FaChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
}
